import type {
  AutomationAction,
  AutomationRule,
  AutomationTrigger,
  CreateAutomationRuleInput,
  TaskPriority,
} from '@pm/schemas';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  Input,
  LabelCaps,
  Select,
  SkeletonRows,
} from '@pm/ui';
import { Plus, Trash2, Zap } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { UserPicker } from '@/components/pickers';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PRIORITY_LABEL } from '@/lib/format';
import {
  useAutomations,
  useFunctions,
  useInvalidating,
  useTags,
  useTaskBlocks,
  useWorkflowTemplates,
  wfKeys,
} from '@/lib/queries';
import { useToast } from '@/lib/toast';

const TRIGGER_LABEL: Record<AutomationTrigger, string> = {
  TASK_CREATED: 'Tarefa criada',
  TASK_COMPLETED: 'Tarefa concluída',
  TASK_APPROVED: 'Tarefa aprovada',
  TASK_REJECTED: 'Tarefa reprovada',
  STAGE_COMPLETED: 'Etapa concluída',
  CONTRACT_ACTIVATED: 'Contrato ativado (Fase 6)',
  EXTERNAL_REQUEST_APPROVED: 'Solicitação externa aprovada (Fase 3)',
  DUE_DATE_REACHED: 'Prazo chegando',
};

const ACTION_LABEL: Record<AutomationAction['type'], string> = {
  CREATE_TASK: 'Criar tarefa',
  CREATE_TASK_BLOCK: 'Criar bloco de tarefas',
  MOVE_STAGE: 'Mover etapa',
  ASSIGN_USER: 'Atribuir responsável',
  SEND_NOTIFICATION: 'Enviar notificação',
  SET_TAG: 'Aplicar tag',
  CREATE_WORKFLOW: 'Iniciar workflow',
};

/** WORKFLOWS §8-§10 — automações parametrizáveis dentro do workflow (sem editor genérico). */
export function AutomationsSection({ workflowTemplateId }: { workflowTemplateId?: string }) {
  const toast = useToast();
  const { can } = useAuth();
  const { data = [], isLoading } = useAutomations();
  const [create, setCreate] = useState(false);
  const [remove, setRemove] = useState<AutomationRule | null>(null);
  const rules = workflowTemplateId
    ? data.filter(
        (r) =>
          !r.conditions.workflow_template_id ||
          r.conditions.workflow_template_id === workflowTemplateId,
      )
    : data;
  const del = useInvalidating(
    (id: string) => api.delete(`/automations/${id}`),
    [wfKeys.automations],
  );
  const toggle = useInvalidating(
    ({ id, status }: { id: string; status: 'ACTIVE' | 'PAUSED' }) =>
      api.patch(`/automations/${id}`, { status }),
    [wfKeys.automations],
  );

  return (
    <div className="flex flex-col gap-gap">
      {can('workflows.manage') ? (
        <div className="flex justify-end">
          <Button variant="primary" onClick={() => setCreate(true)}>
            <Plus /> Nova automação
          </Button>
        </div>
      ) : null}
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : rules.length === 0 ? (
          <EmptyState
            icon={Zap}
            title="Nenhuma automação"
            description="Ex.: quando a etapa Kick-off concluir, criar tarefa de follow-up e notificar o gestor."
          />
        ) : (
          <ul>
            {rules.map((r) => (
              <li
                key={r.id}
                className="flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {r.name}
                    {r.status === 'PAUSED' ? (
                      <Badge tone="neutral" className="ml-2">
                        pausada
                      </Badge>
                    ) : null}
                  </p>
                  <p className="flex flex-wrap gap-1.5 text-sm text-text-2">
                    <Badge tone="accent">{TRIGGER_LABEL[r.trigger]}</Badge>
                    {r.conditions.stage_name ? (
                      <span className="font-mono text-xs">etapa = {r.conditions.stage_name}</span>
                    ) : null}
                    {r.conditions.priority ? (
                      <span className="font-mono text-xs">
                        prioridade = {PRIORITY_LABEL[r.conditions.priority]}
                      </span>
                    ) : null}
                    {r.conditions.hours_before ? (
                      <span className="font-mono text-xs">{r.conditions.hours_before}h antes</span>
                    ) : null}
                    <span className="text-text-3">→</span>
                    {r.actions.map((a, i) => (
                      <Badge key={i} mono={false} tone="neutral">
                        {ACTION_LABEL[a.type]}
                      </Badge>
                    ))}
                  </p>
                </div>
                <span className="font-mono text-xs text-text-3">{r.runs_count} exec.</span>
                {can('workflows.manage') ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        toggle.mutate(
                          { id: r.id, status: r.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE' },
                          { onError: (e) => toast.error(e) },
                        )
                      }
                    >
                      {r.status === 'ACTIVE' ? 'Pausar' : 'Ativar'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Excluir"
                      onClick={() => setRemove(r)}
                    >
                      <Trash2 />
                    </Button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent title="Nova automação" size="lg">
          <AutomationForm workflowTemplateId={workflowTemplateId} onDone={() => setCreate(false)} />
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={remove !== null}
        onOpenChange={() => setRemove(null)}
        title={`Excluir automação ${remove?.name ?? ''}`}
        confirmLabel="Excluir"
        danger
        loading={del.isPending}
        onConfirm={() =>
          remove &&
          del.mutate(remove.id, {
            onSuccess: () => (toast.success('Automação excluída'), setRemove(null)),
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

function AutomationForm({
  workflowTemplateId,
  onDone,
}: {
  workflowTemplateId?: string;
  onDone: () => void;
}) {
  const toast = useToast();
  const { data: functions = [] } = useFunctions();
  const { data: tags = [] } = useTags();
  const { data: blocks = [] } = useTaskBlocks();
  const { data: templates = [] } = useWorkflowTemplates();
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState<AutomationTrigger>('STAGE_COMPLETED');
  const [stageName, setStageName] = useState('');
  const [priority, setPriority] = useState<'' | TaskPriority>('');
  const [hoursBefore, setHoursBefore] = useState('24');
  const [scopedToTemplate, setScopedToTemplate] = useState(Boolean(workflowTemplateId));
  const [actions, setActions] = useState<AutomationAction[]>([
    {
      type: 'SEND_NOTIFICATION',
      params: { to: 'RESPONSIBLE', title: 'Etapa concluída em {{task.title}}' },
    },
  ]);
  const save = useInvalidating(
    (input: CreateAutomationRuleInput) => api.post('/automations', input),
    [wfKeys.automations],
  );

  const setAction = (i: number, a: AutomationAction) =>
    setActions(actions.map((x, j) => (j === i ? a : x)));
  const addAction = (type: AutomationAction['type']) => {
    const blank: Record<AutomationAction['type'], AutomationAction> = {
      CREATE_TASK: {
        type: 'CREATE_TASK',
        params: { title: 'Follow-up de {{task.title}}', priority: 'NORMAL', as_subtask: false },
      },
      CREATE_TASK_BLOCK: {
        type: 'CREATE_TASK_BLOCK',
        params: { block_id: blocks[0]?.id ?? '', as_subtasks: true },
      },
      MOVE_STAGE: { type: 'MOVE_STAGE', params: { to_sort_order: 1 } },
      ASSIGN_USER: {
        type: 'ASSIGN_USER',
        params: { user_id: null, function_id: functions[0]?.id ?? null },
      },
      SEND_NOTIFICATION: {
        type: 'SEND_NOTIFICATION',
        params: { to: 'RESPONSIBLE', title: '{{task.title}}' },
      },
      SET_TAG: { type: 'SET_TAG', params: { tag_id: tags[0]?.id ?? '' } },
      CREATE_WORKFLOW: {
        type: 'CREATE_WORKFLOW',
        params: { workflow_template_id: templates[0]?.id ?? '' },
      },
    };
    setActions([...actions, blank[type]]);
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          {
            name,
            trigger,
            conditions: {
              workflow_template_id: scopedToTemplate ? (workflowTemplateId ?? null) : null,
              stage_name: trigger === 'STAGE_COMPLETED' && stageName ? stageName : null,
              priority: priority || null,
              hours_before: trigger === 'DUE_DATE_REACHED' ? Number(hoursBefore) : null,
            },
            actions,
            status: 'ACTIVE',
          },
          {
            onSuccess: () => (toast.success('Automação criada'), onDone()),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      <Field label="Nome" htmlFor="au-name">
        <Input
          id="au-name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex.: Follow-up após kick-off"
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Quando (gatilho)" htmlFor="au-trigger">
          <Select
            id="au-trigger"
            value={trigger}
            onChange={(e) => setTrigger(e.target.value as AutomationTrigger)}
          >
            {(Object.keys(TRIGGER_LABEL) as AutomationTrigger[])
              .filter((t) => !['CONTRACT_ACTIVATED', 'EXTERNAL_REQUEST_APPROVED'].includes(t))
              .map((t) => (
                <option key={t} value={t}>
                  {TRIGGER_LABEL[t]}
                </option>
              ))}
          </Select>
        </Field>
        {trigger === 'STAGE_COMPLETED' ? (
          <Field label="Nome da etapa (opcional)" htmlFor="au-stage">
            <Input
              id="au-stage"
              value={stageName}
              onChange={(e) => setStageName(e.target.value)}
              placeholder="Ex.: Kick-off"
            />
          </Field>
        ) : null}
        {trigger === 'DUE_DATE_REACHED' ? (
          <Field label="Horas antes do prazo" htmlFor="au-hours">
            <Input
              id="au-hours"
              type="number"
              min={0}
              mono
              value={hoursBefore}
              onChange={(e) => setHoursBefore(e.target.value)}
            />
          </Field>
        ) : null}
        <Field label="Só para prioridade" htmlFor="au-prio">
          <Select
            id="au-prio"
            value={priority}
            onChange={(e) => setPriority(e.target.value as '' | TaskPriority)}
          >
            <option value="">Qualquer</option>
            {(['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const).map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABEL[p]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {workflowTemplateId ? (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={scopedToTemplate}
            onChange={(e) => setScopedToTemplate(e.target.checked)}
          />{' '}
          Só para demandas deste workflow
        </label>
      ) : null}

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <LabelCaps>Então (ações)</LabelCaps>
          <Select
            value=""
            onChange={(e) =>
              e.target.value && addAction(e.target.value as AutomationAction['type'])
            }
            className="ml-auto h-control-sm w-[200px] text-sm"
          >
            <option value="">+ ação</option>
            {(Object.keys(ACTION_LABEL) as AutomationAction['type'][]).map((t) => (
              <option key={t} value={t}>
                {ACTION_LABEL[t]}
              </option>
            ))}
          </Select>
        </div>
        {actions.map((a, i) => (
          <div key={i} className="flex flex-col gap-2 rounded border border-border p-3">
            <div className="flex items-center gap-2">
              <Badge mono={false} tone="accent">
                {ACTION_LABEL[a.type]}
              </Badge>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="ml-auto"
                aria-label="Remover ação"
                onClick={() => setActions(actions.filter((_, j) => j !== i))}
              >
                <Trash2 />
              </Button>
            </div>
            {a.type === 'CREATE_TASK' ? (
              <div className="grid gap-2 sm:grid-cols-2">
                <Input
                  placeholder="Título ({{task.title}} disponível)"
                  value={a.params.title}
                  onChange={(e) =>
                    setAction(i, { ...a, params: { ...a.params, title: e.target.value } })
                  }
                  className="h-control-sm text-sm"
                />
                <Select
                  value={a.params.function_id ?? ''}
                  onChange={(e) =>
                    setAction(i, {
                      ...a,
                      params: { ...a.params, function_id: e.target.value || null },
                    })
                  }
                  className="h-control-sm text-sm"
                >
                  <option value="">Sem função</option>
                  {functions.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </Select>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={0}
                    mono
                    placeholder="SLA"
                    value={a.params.sla_value ?? ''}
                    onChange={(e) =>
                      setAction(i, {
                        ...a,
                        params: {
                          ...a.params,
                          sla_value: e.target.value === '' ? undefined : Number(e.target.value),
                          sla_unit: a.params.sla_unit ?? 'BUSINESS_DAYS',
                        },
                      })
                    }
                    className="h-control-sm w-24 text-sm"
                  />
                  <Select
                    value={a.params.sla_unit ?? 'BUSINESS_DAYS'}
                    onChange={(e) =>
                      setAction(i, {
                        ...a,
                        params: {
                          ...a.params,
                          sla_unit: e.target.value as 'HOURS' | 'DAYS' | 'BUSINESS_DAYS',
                        },
                      })
                    }
                    className="h-control-sm text-sm"
                  >
                    <option value="BUSINESS_DAYS">dias úteis</option>
                    <option value="DAYS">dias</option>
                    <option value="HOURS">horas</option>
                  </Select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={a.params.as_subtask}
                    onChange={(e) =>
                      setAction(i, { ...a, params: { ...a.params, as_subtask: e.target.checked } })
                    }
                  />{' '}
                  Como subtarefa da demanda
                </label>
              </div>
            ) : null}
            {a.type === 'CREATE_TASK_BLOCK' ? (
              <Select
                value={a.params.block_id}
                onChange={(e) =>
                  setAction(i, { ...a, params: { ...a.params, block_id: e.target.value } })
                }
                className="h-control-sm text-sm"
              >
                {blocks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            ) : null}
            {a.type === 'MOVE_STAGE' ? (
              <Input
                type="number"
                min={0}
                mono
                value={a.params.to_sort_order}
                onChange={(e) =>
                  setAction(i, { ...a, params: { to_sort_order: Number(e.target.value) } })
                }
                className="h-control-sm w-32 text-sm"
                placeholder="Nº da etapa (0 = primeira)"
              />
            ) : null}
            {a.type === 'ASSIGN_USER' ? (
              <div className="grid gap-2 sm:grid-cols-2">
                <UserPicker
                  value={a.params.user_id}
                  onChange={(id) => setAction(i, { ...a, params: { ...a.params, user_id: id } })}
                  placeholder="Pela função ↓"
                  size="sm"
                />
                <Select
                  value={a.params.function_id ?? ''}
                  onChange={(e) =>
                    setAction(i, {
                      ...a,
                      params: { ...a.params, function_id: e.target.value || null },
                    })
                  }
                  className="h-control-sm text-sm"
                >
                  <option value="">Sem função</option>
                  {functions.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
            {a.type === 'SEND_NOTIFICATION' ? (
              <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
                <Select
                  value={a.params.to}
                  onChange={(e) =>
                    setAction(i, {
                      ...a,
                      params: { ...a.params, to: e.target.value as typeof a.params.to },
                    })
                  }
                  className="h-control-sm text-sm"
                >
                  <option value="RESPONSIBLE">Responsável</option>
                  <option value="PARTICIPANTS">Participantes</option>
                  <option value="CREATOR">Criador</option>
                  <option value="MANAGERS">Gestores</option>
                </Select>
                <Input
                  placeholder="Título"
                  value={a.params.title}
                  onChange={(e) =>
                    setAction(i, { ...a, params: { ...a.params, title: e.target.value } })
                  }
                  className="h-control-sm text-sm"
                />
              </div>
            ) : null}
            {a.type === 'SET_TAG' ? (
              <Select
                value={a.params.tag_id}
                onChange={(e) => setAction(i, { ...a, params: { tag_id: e.target.value } })}
                className="h-control-sm text-sm"
              >
                {tags.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            ) : null}
            {a.type === 'CREATE_WORKFLOW' ? (
              <Select
                value={a.params.workflow_template_id}
                onChange={(e) =>
                  setAction(i, {
                    ...a,
                    params: { ...a.params, workflow_template_id: e.target.value },
                  })
                }
                className="h-control-sm text-sm"
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            ) : null}
          </div>
        ))}
      </div>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={save.isPending}
          disabled={name.trim().length < 2 || actions.length === 0}
        >
          Criar automação
        </Button>
      </DialogFooter>
    </form>
  );
}
